import { apiUrl } from '../runtime'
import { clearSession, getAccessToken, setAccessToken } from './tokenStore'

let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const response = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) {
        clearSession()
        return null
      }
      const payload = (await response.json()) as { access_token?: string }
      if (!payload.access_token) {
        clearSession()
        return null
      }
      setAccessToken(payload.access_token)
      return payload.access_token
    } catch {
      clearSession()
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

function withAuthHeader(init: RequestInit | undefined, token: string | null): RequestInit {
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return {
    ...init,
    headers,
    credentials: init?.credentials ?? 'same-origin',
  }
}

// NOTE: the 401-retry path replays `init` verbatim. If `init.body` is a
// single-shot stream (ReadableStream, Blob read from a one-time reader),
// the retry will fail because the body has been consumed by the first
// fetch. Today no caller passes a stream body — JSON strings, FormData,
// and URLSearchParams are all safe. Phase 3 may add Request-cloning if
// streaming uploads land.
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = getAccessToken()
  const url = apiUrl(path)
  const initialResponse = await fetch(url, withAuthHeader(init, token))
  if (initialResponse.status !== 401) return initialResponse

  const newToken = await refreshAccessToken()
  if (!newToken) return initialResponse
  return fetch(url, withAuthHeader(init, newToken))
}
